import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import NatDisasterRepo, {DisasterVoidResponse} from './repository/NatDisasterRepo'
import {Nullable} from '../../../app/TypeHelpers'
import React, {Component, ReactElement} from 'react'
import {PlainLinkStyledBackButton, PrimaryStyledButton, StyledSecondaryButton} from '../../../styledComponents/Button'
import {BlockSpacer, currentMedia} from '../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../styledComponents/DisplayItem'
import styled from 'styled-components'
import {ItemHeading, PageTitle, WideWhiteArea} from '../applications/common/resident/NatResidentApplicationsCommon'
import {
    HeadingAreaMessageComponentForResident
} from '../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import PPRadioGroupForResident, {
    ResidentRadioButtonProps
} from '../../../components/shared/forms/resident/PPRadioGroupForResident'
import {CenterRowFlexContainer, ColumnFlexContainer} from '../../../styledComponents/FlexComponents'
import {Colors, FontSize} from '../../../styledComponents/Constants'
import {Messages} from '../../../Messages'
import NatResidentMessageDialogComponent from '../common/resident/NatResidentMessageDialogComponent'
import {DISASTER_MODE_RELEASE_EXTENSION, MESSAGE_TYPE} from '../../../CodeMaster'
import moment from 'moment'
import {HKConstants} from '../../../HKConstants'
import {DisasterModePeriodResponse} from './repository/NatDisasterModePeriodResponse'
import {NatDisasterModeSettingLocationState} from './common/NatDisasterModeSettingLocationState'
import {Box} from '@mui/material'

interface Props {
    condominiumCd: string,
    disasterRepo: NatDisasterRepo,
}

interface State {
    isLoadCompleted: boolean,
    openChangeDialog: boolean,
    cautionChecked: boolean,
    registerButtonDisabled: boolean,
    settingModeSelected: string,
    disasterEndDate: Nullable<string>,
    version: number,
}

class NatResidentDisasterModeReleaseExtensionPage
    extends Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Readonly<Props & ResidentWithErrorHandlingPagePropsSet>) {
        super(props)
        this.state = {
            isLoadCompleted: false,
            openChangeDialog: false,
            cautionChecked: false,
            registerButtonDisabled: true,
            settingModeSelected: '',
            disasterEndDate: null,
            version: 0
        }
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
        this.handleChangeRadio = this.handleChangeRadio.bind(this)
        this.updateRegisterButtonDisabled = this.updateRegisterButtonDisabled.bind(this)
        this.onChangeCautionChecked = this.onChangeCautionChecked.bind(this)
        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleDialogCancelClick = this.handleDialogCancelClick.bind(this)
        this.handleDialogOkClick = this.handleDialogOkClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {disasterRepo, eventEmitter} = this.props
        await this.props.eventEmitter.showLoading();
        try {
            // モード設定状況を取得する
            if (this.props.condominiumCd) {
                const disasterModeSetting = await this.props.asyncCallFunction(
                    disasterRepo.getDisasterMode.bind(disasterRepo, this.props.condominiumCd),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    }
                )
                if (disasterModeSetting) {
                    this.setState({
                        disasterEndDate: disasterModeSetting.disasterEndDate,
                        version: disasterModeSetting.version
                    })
                }
            }
        } finally {
            this.props.eventEmitter.closeLoading();
            this.setState({
                isLoadCompleted: true
            })
        }
    }

    handleBackLinkOnClick(): void {
        this.props.navigate(
            `/resident/condominiums/${this.props.condominiumCd}/disasters/prevention/top`,
            {replace: true}
        )
    }

    // 登録ボタンの活性制御
    updateRegisterButtonDisabled(): void {
        const {settingModeSelected, cautionChecked} = this.state

        let registerButtonDisabled = true
        if (settingModeSelected) {
            switch (settingModeSelected) {
                case DISASTER_MODE_RELEASE_EXTENSION.RELEASE.value :
                    if (cautionChecked) {
                        registerButtonDisabled = false
                    }
                    break;
                case DISASTER_MODE_RELEASE_EXTENSION.EXTENSION.value :
                    registerButtonDisabled = false
                    break;
                default :
                    break;
            }
        }
        this.setState({registerButtonDisabled: registerButtonDisabled})
    }

    // ラジオ項目更新
    handleChangeRadio(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            settingModeSelected: event.target.value,
            cautionChecked: false
        }, () => {
            this.updateRegisterButtonDisabled()
        })
    }

    onChangeCautionChecked(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({cautionChecked: event.target.checked}, () => {
            this.updateRegisterButtonDisabled()
        })
    }

    handleRegisterButtonClick(): void {
        this.setState({openChangeDialog: true})
    }

    handleDialogCancelClick(): void {
        this.setState({openChangeDialog: false})
    }

    async handleDialogOkClick(): Promise<void> {
        const {settingModeSelected} = this.state
        this.setState({openChangeDialog: false})
        if (!settingModeSelected) {
            return;
        }
        await this.props.eventEmitter.showLoading()
        try {
            switch (settingModeSelected) {
                case DISASTER_MODE_RELEASE_EXTENSION.RELEASE.value :
                    await this.releaseDisasterMode();
                    break;
                case DISASTER_MODE_RELEASE_EXTENSION.EXTENSION.value :
                    await this.extensionDisasterMode();
                    break;
                default :
                    break;
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }
    async releaseDisasterMode(): Promise<void> {
        const {eventEmitter, disasterRepo, condominiumCd} = this.props
        const releaseResponse: Nullable<DisasterVoidResponse> = await this.props.asyncCallFunction(
            this.props.disasterRepo.releaseDisasterModeResident.bind(
                disasterRepo, {
                    condominiumCd: condominiumCd,
                    version: this.state.version,
                }),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            }
        )
        if (releaseResponse && releaseResponse.requestError === null) {
            const locationData: NatDisasterModeSettingLocationState = {
                modeSettingType: 'release'
            }
            this.props.navigate(
                `/resident/condominiums/${condominiumCd}/disasters/mode/complete`,
                {replace: true, state: locationData}
            )
        }
    }
    async extensionDisasterMode(): Promise<void> {
        const {eventEmitter, disasterRepo, condominiumCd} = this.props
        const extensionResponse: Nullable<DisasterModePeriodResponse> = await this.props
            .asyncCallFunction(
                this.props.disasterRepo.extensionDisasterModeResident.bind(
                    disasterRepo, {
                        condominiumCd: condominiumCd,
                        version: this.state.version,
                    }),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
        if (extensionResponse?.details && extensionResponse.requestError === null) {
            const locationData: NatDisasterModeSettingLocationState = {
                modeSettingType: 'extension',
                disasterEndDate: extensionResponse.details.disasterEndDate
            }
            this.props.navigate(
                `/resident/condominiums/${condominiumCd}/disasters/mode/complete`,
                {replace: true, state: locationData}
            )
        }
    }

    getRadioGroupDataForReleaseExtension(): ResidentRadioButtonProps[] {

        const createRadioGroupData = (releaseMode: DISASTER_MODE_RELEASE_EXTENSION) =>
            ({
                label:
                    <Box>
                        災害時モードを<StyledDisasterModeInfo>{releaseMode.label}</StyledDisasterModeInfo>
                    </Box>,
                value: releaseMode.value
            })

        const radioButtonProps = []
        /** 災害時モードを今すぐ解除する */
        radioButtonProps.push(createRadioGroupData(
            DISASTER_MODE_RELEASE_EXTENSION.RELEASE))
        /** 災害時モードを延長する */
        radioButtonProps.push(createRadioGroupData(
            DISASTER_MODE_RELEASE_EXTENSION.EXTENSION))
        return radioButtonProps
    }

    renderDisasterEndDate(): ReactElement {
        return (
            <>
                <ItemHeading>現在の災害時モード終了予定日</ItemHeading>
                <DescriptionArea>
                    {moment(this.state.disasterEndDate).format('YYYY年M月D日(ddd)')}
                </DescriptionArea>
            </>
        )
    }

    renderDisasterMode(): ReactElement {
        const {settingModeSelected} = this.state
        return (
            <>
                <ItemHeading>災害時モード設定</ItemHeading>
                <DescriptionArea>
                    マンション内の被害状況から、災害時モードの解除または延長を判断してください。
                    <BlockSpacer height={'20px'}/>
                    <PPRadioGroupForResident
                        data={this.getRadioGroupDataForReleaseExtension()}
                        selectValue={settingModeSelected}
                        onChange={this.handleChangeRadio}
                        eventKeys={['settingModeSelected']}
                        eventEmitter={this.props.eventEmitter}
                    />
                </DescriptionArea>
            </>
        )
    }

    renderCaution(): Nullable<ReactElement> {
        const {settingModeSelected} = this.state
        if (settingModeSelected !== DISASTER_MODE_RELEASE_EXTENSION.RELEASE.value) {
            return null
        }
        return (
            <>
                <CautionBlock>
                    <CautionArea>
                        <CautionTitle>注意事項</CautionTitle>
                        <BlockSpacer height={'5px'}/>
                        <CautionText>災害時モードを解除すると、情報共有を行う機能は利用できなくなります。</CautionText>
                    </CautionArea>
                    <CautionCheckBoxStyled>
                        <CenterRowFlexContainer>
                            <StyledCheckbox
                                type="checkbox"
                                checked={this.state.cautionChecked}
                                onChange={this.onChangeCautionChecked}
                            />
                            <BlockSpacer width={'5px'}/>
                            注意事項を確認しました
                        </CenterRowFlexContainer>
                    </CautionCheckBoxStyled>
                </CautionBlock>
            </>
        )
    }

    renderExtensionDisasterEndDate(): Nullable<ReactElement> {
        const {disasterEndDate, settingModeSelected} = this.state
        const extensionDisasterEndDate = moment(disasterEndDate).clone()
            .add(Number(HKConstants.VMN.DIS.SETTING_PERIOD_MONTH), 'month')
            .format('YYYY年M月D日(ddd)')
        const extensionDisasterEndNoticeDate = moment(disasterEndDate).clone()
            .add(Number(HKConstants.VMN.DIS.SETTING_PERIOD_MONTH), 'month')
            // 災害時モード解除を実行する1週間前の日付を計算(災害時モード終了予定日の翌日から1週間前)
            .subtract(6, 'days')
            .format('YYYY年M月D日(ddd)')
        if (!(settingModeSelected === DISASTER_MODE_RELEASE_EXTENSION.EXTENSION.value)) {
            return null
        }
        return (
            <>
                <BlockSpacer height={'15px'}/>
                <ItemHeading>延長後の災害時モード終了予定日</ItemHeading>
                <DescriptionArea>
                    {extensionDisasterEndDate}
                    <Box height={'20px'}/>
                    <CautionText>
                        ※{extensionDisasterEndNoticeDate}に解除予告の通知が届きます。
                    </CautionText>
                </DescriptionArea>
            </>
        )
    }

    renderRegisterDisasterModeDialog(): ReactElement {
        const {settingModeSelected} = this.state
        let message = ''
        if (settingModeSelected) {
            switch (settingModeSelected) {
                case DISASTER_MODE_RELEASE_EXTENSION.RELEASE.value :
                    message = Messages.IDISR0002.toString('今すぐ解除')
                    break;
                case DISASTER_MODE_RELEASE_EXTENSION.EXTENSION.value :
                    message = Messages.IDISR0002.toString('延長')
                    break;
                default :
                    break;
            }
        }
        return (
            <NatResidentMessageDialogComponent
                open={this.state.openChangeDialog}
                params={{
                    title: '確認',
                    errorLevel: MESSAGE_TYPE.WARNING,
                    message: message,
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: 'キャンセル',
                            buttonHandler: this.handleDialogCancelClick,
                        },
                        {
                            isDefault: false,
                            buttonLabel: 'OK',
                            buttonHandler: this.handleDialogOkClick,
                        },
                    ],
                }}
            />
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }
        return (
            <>
                {this.renderRegisterDisasterModeDialog()}
                <PlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</PlainLinkStyledBackButton>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <MainTextBlock>
                    <BlockSpacer height={'30px'}/>
                    <PageTitle>
                        災害時モード設定
                    </PageTitle>
                    <BlockSpacer height={'20px'}/>
                    災害時モードの設定または解除を行うことができます。
                    <BlockSpacer height={'20px'}/>
                    <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                </MainTextBlock>
                {this.renderDisasterEndDate()}
                <BlockSpacer height={'15px'}/>
                {this.renderDisasterMode()}
                {this.renderCaution()}
                {this.renderExtensionDisasterEndDate()}
                <BlockSpacer height={'30px'}/>
                <PrimaryStyledButton
                    disabled={this.state.registerButtonDisabled}
                    onClick={this.handleRegisterButtonClick}>
                    登録する
                </PrimaryStyledButton>
                <BlockSpacer height={'30px'}/>
                <StyledSecondaryButton onClick={this.handleBackLinkOnClick}>
                    戻る
                </StyledSecondaryButton>
                <BlockSpacer height={'15px'}/>
            </>)
    }
}

export default createResidentWithErrorHandlingPage(NatResidentDisasterModeReleaseExtensionPage)

const MainTextBlock = styled.div`
    width: 100%;
    ${currentMedia.lessThan('sp')`
    width:100%;
  `}
`
const DescriptionArea = styled(WideWhiteArea)`
    padding: 20px 15px;
`
const CautionBlock = styled(ColumnFlexContainer)`
    padding: 0 15px;
    gap: 20px;
    ${currentMedia.lessThan('sp')`
    padding: 0;
  `}
`
const CautionArea = styled(WideWhiteArea)`
    width: 100%;
    margin-top: 15px;
    padding: 10px;
`
const CautionTitle = styled.div`
    color: ${Colors.AccentDark} ;
    font-weight: bold;
    font-size: ${FontSize.Middle};
`
const CautionText = styled.div`
    font-size: ${FontSize.Middle};
`
const CautionCheckBoxStyled = styled.label`
    font-size: ${FontSize.Body};
`
const StyledCheckbox = styled.input`
    width: 18px;
    height: 18px;
`
const StyledDisasterModeInfo = styled.label`
    font-weight: bold;
`
